import Modal from "@/components/Modal";
import PdfViewer from "@/components/PdfViewer";
import SkeletonRow from "@/components/SkeletonRow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatRupiah } from "@/lib/utils";
import { useDataValidasi } from "@/store/dataValidasiStore";
import { DataAset as DataAsetType } from "@/types";
import { Droplets, Home, Zap } from "lucide-react";
import { useState } from "react";

export default function DataAset({
  isPending,
  isValidPermanen,
}: {
  isPending: boolean;
  isValidPermanen: boolean;
}) {
  const { dataListAset, setDataListAset } = useDataValidasi();
  const [showPdf, setShowPdf] = useState("");
  const [selectedRow, setSelectedRow] = useState<DataAsetType | null>(null);

  const handleValidasi = (id: string) => {
    const updatedData = dataListAset.map((item) =>
      item.id_data_aset === id
        ? { ...item, isValidated: !item.isValidated }
        : item,
    );

    setDataListAset(updatedData);

    const dataValidasiString = localStorage.getItem("dataValidasi");
    const previousData = dataValidasiString
      ? JSON.parse(dataValidasiString)
      : {};
    const previousBiodata = previousData.biodata || [];
    const previousKeluarga = previousData.keluarga || [];
    const previousKeluargaTambahan = previousData.keluarga_tambahan || null;
    const previousBerkas = previousData.berkas || [];

    const updatedDataValidasi = {
      biodata: previousBiodata,
      keluarga: previousKeluarga,
      keluarga_tambahan: previousKeluargaTambahan,
      aset: updatedData,
      berkas: previousBerkas,
    };

    localStorage.setItem("dataValidasi", JSON.stringify(updatedDataValidasi));
  };

  if (isPending)
    return (
      <div className="flex flex-col gap-y-3">
        {Array(5)
          .fill(null)
          .map((_, index) => (
            <SkeletonRow key={index} />
          ))}
      </div>
    );

  return (
    <section className="p-2 sm:p-5 flex flex-col gap-4 w-full">
      {/* HEADER SECTION */}
      <div className="flex items-center gap-2 px-1 mb-2">
        <Home className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">
          Daftar Kepemilikan Aset
        </h2>
      </div>

      {/* --- TAMPILAN MOBILE (CARD) --- */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {dataListAset && dataListAset.length > 0 ? (
          dataListAset.map((item, index) => (
            <div
              key={index}
              className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-4 flex flex-col gap-4"
            >
              {/* Header Card: Jenis Aset & Checkbox Validasi */}
              <div className="flex items-start justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                  {item.jenis_aset?.nama_jenis_aset}
                </h3>

                <label className="flex items-center gap-2 cursor-pointer mt-0.5">
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                    Validasi:
                  </span>
                  <Checkbox
                    className={`h-5 w-5 rounded-md transition-colors ${
                      isValidPermanen || item.isValidated
                        ? "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                        : ""
                    }`}
                    checked={isValidPermanen || item.isValidated}
                    onCheckedChange={() =>
                      !isValidPermanen && handleValidasi(item.id_data_aset)
                    }
                  />
                </label>
              </div>

              {/* Body Card: Data Aset Grid */}
              <div className="grid grid-cols-2 gap-y-4 gap-x-3 text-sm">
                <div>
                  <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5" /> Listrik
                  </p>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {item.tegangan_listrik?.tegangan_listrik}{" "}
                    <span className="text-xs text-slate-500">VA</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <Droplets className="w-3.5 h-3.5" /> Air
                  </p>
                  <p className="font-mono font-medium text-slate-800 dark:text-slate-200">
                    {item.pembayaran_air
                      ? formatRupiah(Number(item.pembayaran_air_old))
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Luas Tanah</p>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {item.aset_luas_tanah_old}{" "}
                    <span className="text-xs text-slate-500">m²</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Luas Bangunan</p>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {item.aset_luas_bangunan_old}{" "}
                    <span className="text-xs text-slate-500">m²</span>
                  </p>
                </div>
              </div>

              {/* Footer Card: Tombol Dokumen */}
              <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <Button
                  type="button"
                  variant={item.path_berkas_listrik ? "outline" : "ghost"}
                  size="sm"
                  className={`w-full h-9 ${!item.path_berkas_listrik && "opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-800/50"}`}
                  disabled={!item.path_berkas_listrik}
                  onClick={() => {
                    if (item.path_berkas_listrik) {
                      setShowPdf("path_berkas_listrik");
                      setSelectedRow(item);
                    }
                  }}
                >
                  <Zap
                    className={`w-4 h-4 mr-1.5 ${item.path_berkas_listrik ? "text-amber-500" : ""}`}
                  />
                  Listrik
                </Button>

                <Button
                  type="button"
                  variant={item.path_berkas_air ? "outline" : "ghost"}
                  size="sm"
                  className={`w-full h-9 ${!item.path_berkas_air && "opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-800/50"}`}
                  disabled={!item.path_berkas_air}
                  onClick={() => {
                    if (item.path_berkas_air) {
                      setShowPdf("path_berkas_air");
                      setSelectedRow(item);
                    }
                  }}
                >
                  <Droplets
                    className={`w-4 h-4 mr-1.5 ${item.path_berkas_air ? "text-blue-500" : ""}`}
                  />
                  Air
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center p-8 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-500">
            <Home className="w-10 h-10 mb-3 mx-auto opacity-20" />
            <p className="text-sm">Data aset tidak ditemukan.</p>
          </div>
        )}
      </div>

      {/* --- TAMPILAN DESKTOP (TABEL) --- */}
      <div className="hidden md:block bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="w-full overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Jenis Aset
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <Zap className="w-4 h-4" /> Listrik
                  </div>
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <Droplets className="w-4 h-4" /> Air
                  </div>
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap text-center">
                  Luas Tanah
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap text-center">
                  Luas Bgn.
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                  Dokumen Bukti
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap w-[8rem]">
                  Validasi
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
              {dataListAset && dataListAset.length > 0 ? (
                dataListAset.map((item, index) => (
                  <tr
                    key={index}
                    className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                  >
                    <td className="px-4 py-4 font-medium text-slate-900 dark:text-slate-100">
                      {item.jenis_aset?.nama_jenis_aset}
                    </td>
                    <td className="px-4 py-4 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                      {item.tegangan_listrik?.tegangan_listrik}{" "}
                      <span className="text-xs">VA</span>
                    </td>
                    <td className="px-4 py-4 font-mono font-medium text-slate-800 dark:text-slate-200 whitespace-nowrap">
                      {item.pembayaran_air
                        ? formatRupiah(Number(item.pembayaran_air_old))
                        : "-"}
                    </td>
                    <td className="px-4 py-4 text-center text-slate-600 dark:text-slate-400 whitespace-nowrap">
                      {item.aset_luas_tanah_old}{" "}
                      <span className="text-xs">m²</span>
                    </td>
                    <td className="px-4 py-4 text-center text-slate-600 dark:text-slate-400 whitespace-nowrap">
                      {item.aset_luas_bangunan_old}{" "}
                      <span className="text-xs">m²</span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                        <Button
                          type="button"
                          variant={
                            item.path_berkas_listrik ? "outline" : "ghost"
                          }
                          size="sm"
                          className={`h-8 px-2 text-xs flex-1 sm:flex-none ${!item.path_berkas_listrik && "opacity-50 cursor-not-allowed"}`}
                          disabled={!item.path_berkas_listrik}
                          onClick={() => {
                            if (item.path_berkas_listrik) {
                              setShowPdf("path_berkas_listrik");
                              setSelectedRow(item);
                            }
                          }}
                        >
                          <Zap
                            className={`w-3 h-3 mr-1.5 ${item.path_berkas_listrik ? "text-amber-500" : ""}`}
                          />
                          Listrik
                        </Button>
                        <Button
                          type="button"
                          variant={item.path_berkas_air ? "outline" : "ghost"}
                          size="sm"
                          className={`h-8 px-2 text-xs flex-1 sm:flex-none ${!item.path_berkas_air && "opacity-50 cursor-not-allowed"}`}
                          disabled={!item.path_berkas_air}
                          onClick={() => {
                            if (item.path_berkas_air) {
                              setShowPdf("path_berkas_air");
                              setSelectedRow(item);
                            }
                          }}
                        >
                          <Droplets
                            className={`w-3 h-3 mr-1.5 ${item.path_berkas_air ? "text-blue-500" : ""}`}
                          />
                          Air
                        </Button>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex justify-center items-center h-full">
                        <Checkbox
                          className={`h-6 w-6 rounded-md transition-colors ${
                            isValidPermanen || item.isValidated
                              ? "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                              : ""
                          }`}
                          checked={isValidPermanen || item.isValidated}
                          onCheckedChange={() =>
                            !isValidPermanen &&
                            handleValidasi(item.id_data_aset)
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-slate-500"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <Home className="w-10 h-10 mb-3 opacity-20" />
                      <p>Data aset tidak ditemukan.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ==========================================
          MODAL PDF VIEWER
      ========================================== */}
      <Modal
        show={showPdf.length > 0}
        onClose={() => setShowPdf("")}
        className="w-[95vw] lg:w-[65vw] h-[85vh] p-0 overflow-hidden flex flex-col bg-slate-100 dark:bg-slate-900"
      >
        <div className="flex items-center justify-between px-5 py-3 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 shrink-0 shadow-sm z-10">
          <div className="flex items-center gap-2">
            {showPdf === "path_berkas_listrik" ? (
              <Badge
                variant="outline"
                className="bg-amber-50 text-amber-600 border-amber-200"
              >
                <Zap className="w-4 h-4 mr-1.5" /> Bukti Tagihan Listrik
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-600 border-blue-200"
              >
                <Droplets className="w-4 h-4 mr-1.5" /> Bukti Tagihan Air
              </Badge>
            )}
          </div>
        </div>

        <div className="flex-1 w-full relative bg-slate-100 dark:bg-slate-900">
          <PdfViewer
            url={
              selectedRow?.[
                showPdf as "path_berkas_air" | "path_berkas_listrik"
              ] || ""
            }
          />
        </div>
      </Modal>
    </section>
  );
}
